import { useMemo } from 'react';

import { useTranslation } from '@shared/core';
import { useQueryParamHelper } from '@shared/core/queryString';
import { AccommodationsConsoleConfig } from '@shared/types/Accommodations.types';
import { HotelSearchContextParams, HotelSearchMapLayout, HotelSearchSource, SortOption } from '@shared/constants/accommodations';

type UseHotelSearchConfigReturn = Readonly<{
  source: HotelSearchSource;
  layout: HotelSearchMapLayout;
  config: AccommodationsConsoleConfig;
}>;

export const useHotelSearchConfig = (): UseHotelSearchConfigReturn => {
  const queryParamHelper = useQueryParamHelper();
  const { tq } = useTranslation('accommodationsConsole');

  const source = (queryParamHelper.getValueString(HotelSearchContextParams.source) as HotelSearchSource | null) ?? HotelSearchSource.OTA;
  const layout = (queryParamHelper.getValueString(HotelSearchContextParams.layout) as HotelSearchMapLayout | null) ?? HotelSearchMapLayout.COMPACT;

  const isOtaContext = source === HotelSearchSource.OTA;

  const config = useMemo<AccommodationsConsoleConfig>(() => {
    const baseSortOptions: [SortOption, string][] = [
      [SortOption.TOP_MATCHES, tq`Top Matches`],
      [SortOption.PRICE_LOW_TO_HIGH, tq`Price Low to High`],
      [SortOption.PRICE_HIGH_TO_LOW, tq`Price High to Low`],
      [SortOption.MOST_SAVINGS, tq`Most Savings`],
      [SortOption.DISTANCE, tq`Distance`],
    ];

    const sortOptions: [SortOption, string][] = isOtaContext ? baseSortOptions : [[SortOption.COUPLES_PICKS, tq`Couple's Picks`], ...baseSortOptions];

    return {
      sourceName: source,
      topBar: {
        searchForm: {
          dateRangeEnabled: true,
          occupancySelectorEnabled: true,
        },
        showJoyLogo: true,
        showCloseButton: false,
      },
      layout: {
        mapLayout: { mode: layout },
        skipCouplePicks: isOtaContext,
      },
      map: {
        shouldShowVenueMapPin: !isOtaContext,
      },
      filtersPanel: {
        hideEventNameAndNote: isOtaContext,
        shouldShowJoyTravelBenefitsFilter: false,
      },
      listingsPanel: {
        listingData: {
          shouldFilterOutNonJoyRoomBlocks: false,
          shouldShowAllAccommodations: !isOtaContext,
        },
        sortConfig: {
          sortOptions,
          defaultSortOption: isOtaContext ? SortOption.TOP_MATCHES : SortOption.COUPLES_PICKS,
        },
        listingEntry: {
          roomBlockTag: '',
          shouldShowAddedTag: false,
          shouldShowCouplePicksTag: !isOtaContext,
          shouldShowRoomBlockNoteAndDate: !isOtaContext,
          bookingCta: {
            default: {
              buttonTitle: isOtaContext ? tq`Book on Joy` : tq`See Availability`,
              buttonProps: {
                variant: 'solid',
                intent: 'newPrimary',
                _hover: { backgroundColor: { _: 'newUIBrandPrimary', md: 'buttonNewPrimaryHover' } },
              },
            },
          },
        },
      },
    };
  }, [tq, source, layout, isOtaContext]);

  return { config, source, layout };
};
